import { useAuth0 } from "@auth0/auth0-react";
import CircularProgress from "@mui/material/CircularProgress";
import UserService from 'Lib/API/users/index';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete-red.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import DefaultEmptyMsg from "components/DefaultEmptyMsg/DefaultEmptyMsg";
import AlertDialog from "components/Dialog/index";
import { InputField } from 'components/InputField';
import { LabelField } from "components/LabelField";
import NotAuthorized from 'components/NotAuthorized/index';
import RadioButtons from "components/RadioButtons/RadioButtons";
import Button from 'components/button/Button';
import SideMenuList from 'components/sideMenuList/SideMenuList';
import { useFormik } from "formik";
import { paymentGatewaySchema } from "pages/ValidationSchema/settingsSchema";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { toastr } from "react-redux-toastr";
import SettingHeader from '../../SettingHeader/settingHEader';
import AuthorizeForm from './AuthorizeForm';
import BanQuestForm from './BanQuestForm';
import StripeForm from './StripeForm';
import TranzilaForm from './TranzilaForm';
import { PaymentGatewayActions, PaymentGatewayContainer, PaymentGatewayContent, PaymentGatewayContentAddName, PaymentGatewayContentAddNameField, PaymentGatewayContentAddNameType, PaymentGatewayContentForm, PaymentGatewayContentName, PaymentGatewayHeader, PaymentGatewayTitle } from './Theme';


const PaymentGateways = ({ heading }) => {
  const { getAccessTokenSilently } = useAuth0();

  let orgInfo =
    localStorage.getItem("authorizedModules") !== undefined
      ? JSON.parse(localStorage.getItem("authorizedModules"))
      : [];

  const [IsSettingsActive, setIsSettingsActive] = useState(true);
  const [selectedPaymentGateway, setSelectedPaymentGateway] = useState(null);
  const [viewType, setViewType] = useState("view");
  const [paymentGateways, setPaymentGateways] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addPaymentGatewayLoading, setAddPaymentGatewayLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: null,
    isLoading: false,
    text: ""
  });
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      selectedGateway: "Stripe",
      payment_gateway_name: "",
      stripe: {
        stripe_key: "",
        publicKey: ""
      },
      authorize: {
        api_login_id: "",
        transaction_key: ""
      },
      banquest: {
        source_key: "",
        pin: ""
      },
      tranzila: {
        terminal_name: "",
        app_key: "",
        secret_key: ""
      }
    },
    validationSchema: paymentGatewaySchema,
    onSubmit: (values) => {
      addPaymentGatewayHandler(values);
    }
  });

  const addPaymentGatewayHandler = useCallback(async (values) => {
    setAddPaymentGatewayLoading(true);
    const token = await getAccessTokenSilently();
    const payload = {
      provider: values.selectedGateway,
      title: `${values.selectedGateway}-${values.payment_gateway_name}`,
      keys: {}
    }
    if (values.selectedGateway === "Stripe") {
      payload.keys = {
        stripe_key: values.stripe.stripe_key,
        publicKey: values.stripe.publicKey
      }
    }
    if (values.selectedGateway === "Authorize") {
      payload.keys = {
        api_login_id: values.authorize.api_login_id,
        transaction_key: values.authorize.transaction_key
      }
    }
    if (values.selectedGateway === "BanQuest") {
      payload.keys = {
        source_key: values.banquest.source_key,
        pin: values.banquest.pin
      }
    }
    if (values.selectedGateway === "Tranzila") {
      payload.keys = {
        terminal_name: values.tranzila.terminal_name,
        app_key: values.tranzila.app_key,
        secret_key: values.tranzila.secret_key
      }
    }
    UserService.addPaymentGateway({ token, data: payload })
      .then((response) => {
        toastr.success("Success", "Payment Gateway added successfully");
        getPaymentGatewaysHandler();
        resetForm();
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setAddPaymentGatewayLoading(false);
      });
  }, []);


  const getPaymentGatewaysHandler = useCallback(async () => {
    setIsLoading(true);
    const token = await getAccessTokenSilently();
    UserService.getPaymentGateways({ token })
      .then((response) => {
        const filteredPaymentGateways = response?.result?.map((item) => {
          return {
            value: item?._id,
            label: item?.title,
            provider: item?.provider
          }
        });
        setPaymentGateways(filteredPaymentGateways);
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    getPaymentGatewaysHandler();
  }, []);

  const onPaymentGatewaySelect = useCallback((paymentGateway) => {
    if(viewType === "add") {
      cancelAddPaymentGateway();
      return;
    }
    setSelectedPaymentGateway(paymentGateway?.value);
    formik.setFieldValue('payment_gateway_name', paymentGateway?.label);
    formik.setFieldValue('selectedGateway', paymentGateway?.provider);
  }, [viewType]);
  useEffect(() => {
    let check = orgInfo?.map((item) => {
      if (item?.moduleName == "financial setting") {
        return setIsSettingsActive(item?.active);
      }
    });
  }, []);

  const onAddPaymentGateway = () => {
    changeViewType("add");
    resetForm("add");
  }

  const changeViewType = (type) => {
    setViewType(type);
  }

  const resetForm = (type) => {
    formik.resetForm();
    setSelectedPaymentGateway(null);
    setViewType(type || "view");
  }

  const cancelAddPaymentGateway = () => {
    setConfirmModal((prev) => (
      {
        ...prev,
        open: true,
        type: "discard",
        text: "Do you want to discard the changes you made?"
      }
    ));
  }

  const onDeletePaymentGateway = () => {
    setConfirmModal((prev) => (
      {
        ...prev,
        open: true,
        type: "delete",
        text: "Do you want to delete the Payment Gateway?"
      }
    ));
  }

  
  const onConfirmModalAccept = () => {
    if (confirmModal.type === "discard") {
      changeViewType("view");
    }
    if (confirmModal.type === "delete") {
      deletePaymentGatewayHandler();
    }
    closeConfirmModal();
  }
  
  const closeConfirmModal = () => {
    setConfirmModal((prev) => (
      {
        ...prev,
        open: false,
        type: null,
        text: "",
        isLoading: false
      }
    ));
  }

  const deletePaymentGatewayHandler = useCallback(async () => {
    setConfirmModal((prev) => (
      {
        ...prev,
        isLoading: true
      }
    ));
    const token = await getAccessTokenSilently();
    UserService.deletePaymentGateway({ token, id: selectedPaymentGateway })
      .then((response) => {
        toastr.success("Success", "Payment Gateway deleted successfully");
        getPaymentGatewaysHandler();
        resetForm();
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setConfirmModal((prev) => (
          {
            ...prev,
            isLoading: false
          }
        ));
      });
  }, [selectedPaymentGateway]);
  
    

  const isDisabled = useMemo(() => {
    return !formik.isValid || !formik.dirty || addPaymentGatewayLoading;
  }, [formik.isValid, formik.dirty, addPaymentGatewayLoading]);


  return IsSettingsActive == false ? (
    <NotAuthorized title={"Reports"} />
  ) : (
    <div className="w-100 ">
      <SettingHeader
        heading={heading}
        path={"/financial-setting/provider-info/view"}
        edit={false}
        addNew={false}
        parentHeading={"Financial"}
        alertOnEdit={false}
      />
      <div className="main-setting__wrapper">
        <div className="setting-main">
          <div className="left-menu-div">
            <div className='w-100'>
              <Button
                size="md"
                variant="primary"
                className={"mt-5 mb-3"}
                style={{
                  marginLeft: "25px",
                }}
                onClick={() => onAddPaymentGateway()}
                disabled={viewType === "add"}
              >
                <PlusIcon />
                Payment Gateway
              </Button>
            </div>
            <SideMenuList
              list={paymentGateways}
              selected={selectedPaymentGateway}
              onClick={(paymentGateway) => onPaymentGatewaySelect(paymentGateway)}
            />
          </div>
          <PaymentGatewayContainer className="settings-details">
            {(selectedPaymentGateway || viewType === "add") ? (
              <>
                <PaymentGatewayHeader>
                  <PaymentGatewayTitle>
                    {selectedPaymentGateway?.label}
                  </PaymentGatewayTitle>
                  <PaymentGatewayActions>
                    {viewType === "view" && (
                      <>
                        <Button
                          size='sm'
                          variant='transparent'
                          style={{
                            color: "#f1585e",
                            padding: "0",
                            fontWeight: "400",
                            fontSize: "14px"
                          }}
                          onClick={() => onDeletePaymentGateway()}
                        >
                          <DeleteIcon color={"#f1585e"} />
                          <span>Delete</span>
                        </Button>
                      </>
                    )}
                    {viewType !== "view" && (
                      <>
                        <Button
                          size="md"
                          variant="primary-outline"
                          onClick={() => cancelAddPaymentGateway()}
                          style={{ minWidth: 80 }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="md"
                          variant="primary"
                          onClick={() => {
                            if (isDisabled) return;
                            formik.handleSubmit();
                          }}
                          style={{ minWidth: 80 }}
                          disabled={isDisabled}
                        >
                          {addPaymentGatewayLoading ? (
                            <div className="d-flex">
                              <CircularProgress
                                size={22}
                                style={{
                                  color: "#ffffff",
                                }}
                              />
                            </div>
                          ) : "Save"}
                        </Button>
                      </>
                    )}
                  </PaymentGatewayActions>
                </PaymentGatewayHeader>
                <PaymentGatewayContent>
                  {viewType === "add" && (
                    <div>
                      <LabelField
                        title="Payment gateway"
                        hint="Choose which payment gateway will process transactions for this credential set. The credential fields below change based on your selection."
                      />
                      <RadioButtons
                        value={formik.values?.selectedGateway}
                        name={"address-labels"}
                        onChange={(value) => formik.setFieldValue('selectedGateway', value)}
                        defaultValue={"active-address"}
                        size={"small"}
                        row={true}
                        options={[
                          {
                            label: "Stripe",
                            value: "Stripe"
                          },
                          {
                            label: "Authorize",
                            value: "Authorize"
                          },
                          {
                            label: "BanQuest",
                            value: "BanQuest"
                          },
                          {
                            label: "Tranzila",
                            value: "Tranzila"
                          }
                        ]}
                      />
                    </div>
                  )}
                  <Row>
                    <Col className="col-sm-6">
                      {viewType === "add" ? (
                        <>
                          <LabelField
                            title="Name"
                            mandatory={true}
                          />
                          <PaymentGatewayContentAddName>
                            <PaymentGatewayContentAddNameType>
                              {formik.values?.selectedGateway}-
                            </PaymentGatewayContentAddNameType>
                            <PaymentGatewayContentAddNameField
                              placeholder="Name"
                              value={formik.values?.payment_gateway_name}
                              onChange={(e) => {
                                formik.setFieldValue('payment_gateway_name', e.target?.value);
                              }}
                            >

                            </PaymentGatewayContentAddNameField>
                          </PaymentGatewayContentAddName>
                        </>
                      ) : (
                        <PaymentGatewayContentName
                          disabled={viewType === "view"}
                        >
                          <InputField
                            mandatory={true}
                            {...{
                              formik,
                              title: 'Name',
                              name: 'Name',
                              placeholder: 'Name',
                              checkValidation: true,
                              type: "text"
                            }}
                            value={formik.values?.payment_gateway_name}
                            handleChange={(e) => {
                              formik.setFieldValue('payment_gateway_name', e.target?.value);
                            }}
                          />
                        </PaymentGatewayContentName>
                      )}
                    </Col>
                  </Row>
                  <PaymentGatewayContentForm
                    disabled={viewType === "view"}
                  >
                    {formik.values?.selectedGateway === "Stripe" && (
                      <StripeForm
                        formik={formik}
                        disabled={viewType === "view"}
                      />
                    )}
                    {formik.values?.selectedGateway === "Authorize" && (
                      <AuthorizeForm
                        formik={formik}
                        disabled={viewType === "view"}
                      />
                    )}
                    {formik.values?.selectedGateway === "BanQuest" && (
                      <BanQuestForm
                        formik={formik}
                        disabled={viewType === "view"}
                      />
                    )}
                    {formik.values?.selectedGateway === "Tranzila" && (
                      <TranzilaForm
                        formik={formik}
                        disabled={viewType === "view"}
                      />
                    )}
                  </PaymentGatewayContentForm>
                </PaymentGatewayContent>
              </>
            ) : (
              <div className="mt-40 ledgers-settings-empty">
                <DefaultEmptyMsg msg="No payment gateway selected" size="30px" />
              </div>
            )}
          </PaymentGatewayContainer>
        </div>
      </div>
      <AlertDialog
        open={confirmModal.open}
        onDecline={closeConfirmModal}
        onAccept={onConfirmModalAccept}
        title={"Are you sure?"}
        description={confirmModal.text}
      />
    </div>
  )
}

export default PaymentGateways